import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useCanAccess } from '@/hooks/useCanAccess';
import { useMigrateToUazapi, useWhatsAppStatus } from '@/hooks/useWhatsApp';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/v2/design-system/primitives';

/**
 * Story 53.20 — starts the manual-recovery migration for a clinic still on Z-API.
 * The backend verifies its private snapshot before clearing operational history or
 * exposing the new QR. Only WhatsApp managers see it. Hotfix 2026-09-15: the banner
 * is fixed (no snooze/dismiss) until the clinic leaves Z-API or the migration starts.
 */
export function WhatsAppUpgradeNoticeBanner() {
  const navigate = useNavigate();
  const podeGerenciar = useCanAccess('whatsapp:manage');
  const { data: status } = useWhatsAppStatus(podeGerenciar);
  const migrate = useMigrateToUazapi();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const migrationInFlight = status?.migration?.status === 'pending' || status?.migration?.status === 'uazapi_connected';
  if (!podeGerenciar || status?.provider !== 'zapi' || migrationInFlight) return null;

  const confirm = () => {
    migrate.mutate(undefined, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/config/whatsapp');
      },
    });
  };

  return (
    <>
      <div role="status" className="flex flex-wrap items-start gap-3 border-b border-sober-outline bg-sober-surface px-4 py-3 text-so-14 text-sober-text lg:px-8">
        <Sparkles className="mt-0.5 size-4 shrink-0 text-sober-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="font-bold">Nova conexão do WhatsApp disponível — migração necessária</div>
          <p className="mt-1 text-sober-secondary">
            Este aviso fica visível até a migração ser concluída. Vamos preparar uma cópia privada verificada, encerrar a conexão atual e mostrar um novo QR Code. Depois do pareamento, o WhatsApp pode importar o histórico em alguns minutos.
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="min-h-11 rounded-so-sm bg-sober-primary-fg px-3 py-1.5 text-so-12 font-bold text-white hover:opacity-90 sm:min-h-0"
            >
              Preparar novo QR
            </button>
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Preparar nova conexão</DialogTitle>
            <DialogDescription>A cópia privada é verificada antes de qualquer limpeza.</DialogDescription>
          </DialogHeader>
          <DialogBody>
            <ol className="list-decimal space-y-2 pl-5 text-so-14 text-sober-text">
              <li>Salvamos uma cópia privada verificável e encerramos a conexão atual antes do novo QR.</li>
              <li>Mensagens e anotações atuais ficam na cópia de segurança. As conversas serão reiniciadas; pacientes e outros cadastros permanecem.</li>
              <li>Você escaneia um QR Code novo com o celular. O WhatsApp pode importar o histórico em alguns minutos; a recuperação da cópia é manual.</li>
            </ol>
          </DialogBody>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="min-h-11 rounded-so-sm px-3 py-1.5 text-so-12 font-bold text-sober-secondary hover:bg-sober-muted sm:min-h-0"
            >
              Agora não
            </button>
            <button
              type="button"
              onClick={confirm}
              disabled={migrate.isPending}
              className="min-h-11 rounded-so-sm bg-sober-primary-fg px-3 py-1.5 text-so-12 font-bold text-white hover:opacity-90 disabled:opacity-50 sm:min-h-0"
            >
              Preparar novo QR, tenho o celular em mãos
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
